import { useEffect, useRef, useCallback } from "react";
import { useSportsData } from "../contexts/SportsDataContext";
import { useFavorites } from "../contexts/FavoritesContext";
import { useAuth } from "../contexts/AuthContext";

const NOTIF_KEY = "guys_sports_notifs_enabled";

export function useScoreNotifications() {
  const { allGames, changedScores } = useSportsData();
  const { isFavorite } = useFavorites();
  const { user } = useAuth();
  const notifiedRef = useRef(new Set());
  const permissionRef = useRef(typeof Notification !== "undefined" ? Notification.permission : "denied");

  const requestPermission = useCallback(async () => {
    if (typeof Notification === "undefined") return false;
    if (Notification.permission === "granted") return true;
    if (Notification.permission === "denied") return false;
    const result = await Notification.requestPermission();
    permissionRef.current = result;
    return result === "granted";
  }, []);

  const isEnabled = useCallback(() => {
    try { return localStorage.getItem(NOTIF_KEY) === "true"; } catch { return false; }
  }, []);

  const setEnabled = useCallback(async (val) => {
    if (val) {
      const granted = await requestPermission();
      if (!granted) return false;
    }
    localStorage.setItem(NOTIF_KEY, val ? "true" : "false");
    return true;
  }, [requestPermission]);

  // Watch for score changes in favorite games
  useEffect(() => {
    if (!isEnabled()) return;
    // Read permission fresh — user may have granted it mid-session
    const currentPermission = typeof Notification !== "undefined" ? Notification.permission : "denied";
    if (currentPermission !== "granted") return;

    const userFavTeams = user?.preferences?.favorite_teams || [];
    const changedIds = Object.keys(changedScores);
    if (changedIds.length === 0) return;

    for (const gameId of changedIds) {
      if (notifiedRef.current.has(`${gameId}-${changedScores[gameId]}`)) continue;
      notifiedRef.current.add(`${gameId}-${changedScores[gameId]}`);

      const game = allGames.find(g => g.id === gameId);
      if (!game || game.statusState !== "in") continue;

      // Check if this game involves a favorite team
      const isFav = isFavorite(gameId) || userFavTeams.some(ft =>
        ft.id === game.homeTeam?.id || ft.id === game.awayTeam?.id ||
        ft.abbreviation === game.homeTeam?.abbreviation || ft.abbreviation === game.awayTeam?.abbreviation
      );

      if (isFav) {
        try {
          new Notification(`${game.leagueName}: Score Update`, {
            body: `${game.awayTeam?.abbreviation} ${game.awayTeam?.score} - ${game.homeTeam?.abbreviation} ${game.homeTeam?.score} (${game.statusDetail})`,
            icon: game.homeTeam?.logo || game.awayTeam?.logo || "",
            tag: `score-${gameId}`,
            silent: false,
          });
        } catch {}
      }
    }
  }, [changedScores, allGames, isFavorite, user, isEnabled]);

  // Cleanup old notifications
  useEffect(() => {
    const interval = setInterval(() => {
      if (notifiedRef.current.size > 200) notifiedRef.current.clear();
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  return { requestPermission, isEnabled, setEnabled };
}
